import { Box, Button, Icon, Link } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { HelpLink, InitialScreen } from '@nimbus-ds/patterns';
import { FEATURE_ALLOW_CUSTOM_DOMAIN } from 'App/features';
import { useNavegate } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import { IonPageStratus } from 'commons/components';
import { DOMAINS_ADD_DOMAIN_BTN } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { configurationsRoutes } from 'domains/Configurations';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import {
  AddDomainModal,
  DomainsLandingPageSkeleton,
  OurDomainCard,
  OrderProcessingAlert,
  OrderErrorAlert,
} from './components';
import {
  useDomainsLandingPage,
  useDomainsRedirectLogic,
  useActiveOrderPolling,
} from './hooks';
import illustrations from './img/illustrations.png';
import {
  DOMAIN_ORDER_PROCESSING_STATUSES,
  DOMAIN_ORDER_ERROR_STATUSES,
} from '../../constants';
import { domainsApi, useGetActiveDomainOrderQuery } from '../../domainsApi';
import { trackingAddDomainClick } from '../../tracking';
import {
  AddDomainModal as AddExistingDomainModal,
  AttachDomainModal,
  EditOurDomainModal,
} from '../DomainsListPage/components';
import './DomainsLandingPage.scss';

export function DomainsLandingPage() {
  const t = useTranslationConfigurations('domains');
  const dispatch = useAppDispatch();
  const { goTo } = useNavegate();

  const { isLoading, ourDomain } = useDomainsRedirectLogic();
  const { data: activeOrder } = useGetActiveDomainOrderQuery();

  useActiveOrderPolling({
    activeOrder,
    enabled: !isLoading,
  });

  const {
    showAddDomainModal,
    closeAddDomainModal,
    showAddExistingDomainModal,
    openAddExistingDomainModal,
    closeAddExistingDomainModal,
    showAttachDomainModal,
    createdDomainInfo,
    resetCreateDomain,
    openAddDomainModal,
    setCreatedDomainInfo,
    closeAttachDomainModal,
    showEditOurDomainModal,
    openEditOurDomainModal,
    closeEditOurDomainModal,
    initialSearchQuery,
    setInitialSearchQuery,
  } = useDomainsLandingPage();

  const handleOpenAddDomainModal = useUpsellFlow({
    title: t('upsell.addDomain'),
    featureKey: FEATURE_ALLOW_CUSTOM_DOMAIN,
    callback: () => {
      trackingAddDomainClick();
      openAddDomainModal();
    },
    trackingSource: DOMAINS_ADD_DOMAIN_BTN,
  });

  const handleCloseAttachDomainModal = () => {
    closeAttachDomainModal();
    setCreatedDomainInfo(null);
    resetCreateDomain();
    dispatch(domainsApi.util.invalidateTags(['domainsList']));
    goTo(configurationsRoutes.domainsList);
  };

  const handleRetryPayment = () => {
    if (activeOrder) {
      setInitialSearchQuery(activeOrder.domainName);
      openAddDomainModal();
    }
  };

  const handleRedirectToActiveOrder = () => {
    if (activeOrder) {
      goTo(
        configurationsRoutes.domainsOrder.replace(
          ':payOrderId',
          activeOrder.payOrderId,
        ),
      );
    }
  };

  const showProcessingCard =
    activeOrder &&
    DOMAIN_ORDER_PROCESSING_STATUSES.includes(activeOrder.status);

  const showErrorCard =
    activeOrder && DOMAIN_ORDER_ERROR_STATUSES.includes(activeOrder.status);

  if (isLoading) {
    return <DomainsLandingPageSkeleton />;
  }

  return (
    <>
      <IonPageStratus width="medium">
        <Box
          data-style="stratus--domains-landing-page"
          display="flex"
          flexDirection="column"
          gap="8"
        >
          <InitialScreen>
            <InitialScreen.Hero
              title={t('landing.title')}
              subtitle={t('landing.subtitle')}
              description={t('landing.description')}
              image={<img src={illustrations} width="100%" height="auto" />}
              actions={
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap="5"
                  data-style="actions-container"
                >
                  <OurDomainCard
                    ourDomain={ourDomain}
                    onEdit={openEditOurDomainModal}
                  />
                  {showProcessingCard && (
                    <OrderProcessingAlert domainName={activeOrder.domainName} />
                  )}
                  {showErrorCard && (
                    <OrderErrorAlert
                      domainName={activeOrder.domainName}
                      payOrderId={activeOrder.payOrderId}
                      onRetry={handleRetryPayment}
                    />
                  )}
                  <Button
                    as="a"
                    appearance="primary"
                    onClick={handleOpenAddDomainModal}
                  >
                    {t('landing.add_domain_button')}
                  </Button>
                </Box>
              }
            />
          </InitialScreen>
          <HelpLink>
            <Link
              as="a"
              href="https://ayuda.tiendanube.com/es_MX/dominios/guia-configurar-el-dominio-de-la-tienda"
              target="_blank"
              appearance="primary"
              textDecoration="none"
            >
              {t('landing.help_link')}
              <Icon source={<ExternalLinkIcon />} color="currentColor" />
            </Link>
          </HelpLink>
        </Box>
      </IonPageStratus>
      {showAddDomainModal && (
        <AddDomainModal
          onClose={closeAddDomainModal}
          onOpenAddExistingDomain={openAddExistingDomainModal}
          initialSearchQuery={initialSearchQuery}
          hasActiveProcessingOrder={Boolean(showProcessingCard)}
          onRedirectToActiveOrder={handleRedirectToActiveOrder}
        />
      )}
      <AddExistingDomainModal
        show={showAddExistingDomainModal}
        onClose={closeAddExistingDomainModal}
        source="landing"
      />
      {createdDomainInfo && (
        <AttachDomainModal
          show={showAttachDomainModal}
          domainName={createdDomainInfo.name}
          domainId={createdDomainInfo.id}
          supportsOurDns={createdDomainInfo.supportsOurDns}
          isSubdomain={createdDomainInfo.isSubdomain}
          ourDomain={ourDomain}
          onClose={handleCloseAttachDomainModal}
        />
      )}
      <EditOurDomainModal
        show={showEditOurDomainModal}
        currentDomain={ourDomain}
        onClose={closeEditOurDomainModal}
      />
    </>
  );
}
